import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Divider, ListItemIcon, ListItemText,
} from '@mui/material'
import { CreateNewFolder } from '@mui/icons-material'

const PLANTS = [
  { code: 'FBO', label: 'FBO — Faribault, MN' },
  { code: 'TUC', label: 'TUC — Tucson, AZ' },
]

export default function CreatePlanModal({
  open, onClose, onSubmit, folders, onCreateFolder, loading, error,
}) {
  const [form, setForm] = useState({ name: '', description: '', plant: 'FBO', folderId: '' })
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))
  const valid = form.name.trim().length > 0 && form.plant

  const handleSubmit = () => {
    if (!valid) return
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim(),
      plant: form.plant,
      folderId: form.folderId || undefined,
    })
  }

  const handleFolderChange = (e) => {
    if (e.target.value === '__new__') {
      setNewFolderName(new Date().toISOString().slice(0, 10))
      setCreatingFolder(true)
    } else {
      set('folderId')(e)
    }
  }

  const confirmNewFolder = () => {
    const name = newFolderName.trim()
    if (name && onCreateFolder) {
      onCreateFolder(name)
    }
    setCreatingFolder(false)
    setNewFolderName('')
  }

  const handleClose = () => {
    setForm({ name: '', description: '', plant: 'FBO', folderId: '' })
    setCreatingFolder(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Plan</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Plan Name"
          required
          value={form.name}
          onChange={set('name')}
          inputProps={{ maxLength: 120 }}
          helperText={`${form.name.length}/120`}
          autoFocus
        />

        <TextField
          label="Description"
          value={form.description}
          onChange={set('description')}
          multiline
          rows={3}
        />

        <FormControl required>
          <InputLabel>Plant</InputLabel>
          <Select value={form.plant} label="Plant" onChange={set('plant')}>
            {PLANTS.map((p) => (
              <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {creatingFolder ? (
          <TextField
            autoFocus
            label="New Folder Name"
            size="small"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmNewFolder()
              if (e.key === 'Escape') setCreatingFolder(false)
            }}
            onBlur={confirmNewFolder}
          />
        ) : (
          <FormControl>
            <InputLabel>Folder</InputLabel>
            <Select value={form.folderId} label="Folder" onChange={handleFolderChange}>
              <MenuItem value="">(None)</MenuItem>
              {folders.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
              <Divider />
              <MenuItem value="__new__">
                <ListItemIcon><CreateNewFolder fontSize="small" /></ListItemIcon>
                <ListItemText>New Folder</ListItemText>
              </MenuItem>
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || loading}
          startIcon={loading ? <CircularProgress size={18} /> : undefined}
        >
          Create Plan
        </Button>
      </DialogActions>
    </Dialog>
  )
}
